import { useEffect, useState } from "react"
import { DocumentContractuelProduitDto } from "api/documents"
import { documentsAPI } from "services/swagger-api"

/**
 * Une requête par produit pour toute la page : chaque carte d'un même produit (formules,
 * contrats collectifs) partage la même promesse. Un échec n'est pas mémorisé, la carte
 * suivante retentera.
 */
const documentsByProduct = new Map<string, Promise<DocumentContractuelProduitDto[]>>()

export const loadProductContractualDocuments = (productId: string): Promise<DocumentContractuelProduitDto[]> => {
  const cached = documentsByProduct.get(productId)
  if (cached) return cached

  const request = documentsAPI
    .getProductContractualDocuments({ produitId: productId })
    .then((response) => response.items ?? [])
    .catch((error) => {
      documentsByProduct.delete(productId)
      console.error(error)
      return []
    })
  documentsByProduct.set(productId, request)
  return request
}

/**
 * Documents à afficher sur une carte : ceux du collectif pour un contrat collectif, ceux
 * de l'individuel sinon ; si le produit n'en a aucun du bon périmètre, tous.
 */
export const documentsForScope = (
  documents: DocumentContractuelProduitDto[],
  isGroup: boolean
): DocumentContractuelProduitDto[] => {
  const scoped = documents.filter((document) => document.isGroup === isGroup)
  return scoped.length > 0 ? scoped : documents
}

/** Vide le cache (tests). */
export const resetProductContractualDocuments = () => documentsByProduct.clear()

/** Documents contractuels actifs du produit (CG, CS, Notice, IPID, fiche produit) ; vide tant qu'ils chargent. */
export const useProductContractualDocuments = (productId?: string | null): DocumentContractuelProduitDto[] => {
  const [documents, setDocuments] = useState<DocumentContractuelProduitDto[]>([])

  useEffect(() => {
    if (!productId) {
      setDocuments([])
      return
    }
    let isActive = true
    loadProductContractualDocuments(productId).then((items) => {
      if (isActive) setDocuments(items)
    })
    return () => {
      isActive = false
    }
  }, [productId])

  return documents
}
